// WFHLIST point 2: the searchable country field, replacing the dropdown.
//
// ONLY A COUNTRY FROM THE LIST CAN BE SUBMITTED. The text box is a FILTER, never the value: what
// the parent receives through onChange is always a stored country string or '' - typing a free
// value and walking away leaves the selection empty, and the form's own check then refuses to
// submit. That is deliberate and is the point of a combobox over a free text input: the server
// refuses an unlisted value too (wfh_country.is_permitted), so a field that could submit raw text
// would only ever produce a 400.
//
// KEYBOARD: arrows move the active option, Enter chooses it, Escape closes the list. Implemented
// against the ARIA combobox pattern - role="combobox" with aria-expanded and aria-activedescendant
// on the input, role="listbox" and role="option" on the list - so a screen reader announces the
// active option as it changes rather than only on selection.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { matches, sortedCountries } from './wfhCountryLabel';

function CountryTypeahead({
  id,
  countries,
  value,
  onChange,
  inputStyle,
  labelStyle,
  dark = false,
  labelKey = 'leaveForm.wfhCountryLabel',
}) {
  const { t, i18n } = useTranslation();
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const boxRef = useRef(null);

  const lang = (i18n && i18n.language) || 'en';
  const options = useMemo(() => sortedCountries(t, countries, lang),
                          [t, countries, lang]);
  const shown = useMemo(() => options.filter(o => matches(o.label, query) || matches(o.stored, query)),
                        [options, query]);

  // The chosen country's LABEL is what the box shows when the list is closed, so the field reads
  // as a value rather than as a leftover search string.
  const selectedLabel = useMemo(() => {
    const hit = options.find(o => o.stored === value);
    return hit ? hit.label : '';
  }, [options, value]);

  // Clicking away closes the list WITHOUT choosing anything, which is what keeps a half-typed
  // free value from ever becoming the selection.
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) {
        setOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  useEffect(() => { setActive(0); }, [query]);

  const choose = (opt) => {
    if (!opt) return;
    onChange(opt.stored);
    setQuery('');
    setOpen(false);
  };

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      if (!open) { setOpen(true); return; }
      if (shown.length === 0) return;
      setActive(prev => {
        const next = e.key === 'ArrowDown' ? prev + 1 : prev - 1;
        // Wraps, so a keyboard user never reaches a dead end at either end of the list.
        return (next + shown.length) % shown.length;
      });
      return;
    }
    if (e.key === 'Enter') {
      // PREVENTED ALWAYS when the list is open, so Enter chooses an option instead of submitting
      // the surrounding form - picking a country and saving the declaration are two decisions.
      if (open && shown.length > 0) {
        e.preventDefault();
        choose(shown[active]);
      }
      return;
    }
    if (e.key === 'Escape') {
      if (open) {
        e.preventDefault();
        setOpen(false);
        setQuery('');
      }
    }
  };

  const listId = `${id}-listbox`;
  const optionId = (i) => `${id}-option-${i}`;

  return (
    <div ref={boxRef} style={{ position: 'relative' }}>
      <label style={labelStyle} htmlFor={id}>{t(labelKey)}</label>
      <input
        id={id}
        data-testid="wfh-country"
        type="text"
        role="combobox"
        autoComplete="off"
        aria-expanded={open ? 'true' : 'false'}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={open && shown.length > 0 ? optionId(active) : undefined}
        placeholder={t('leaveForm.wfhCountryPlaceholder')}
        style={inputStyle}
        value={open ? query : selectedLabel}
        onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
      />
      {open && (
        <ul
          id={listId}
          role="listbox"
          data-testid="wfh-country-list"
          style={{
            position: 'absolute', zIndex: 20, left: 0, right: 0, top: '100%',
            margin: '4px 0 0', padding: '4px 0', listStyle: 'none', maxHeight: '220px',
            overflowY: 'auto', borderRadius: '6px',
            background: dark ? '#243058' : '#fff',
            border: dark ? '1px solid rgba(201,168,76,0.3)' : '1px solid #cdd4de',
            boxShadow: '0 6px 18px rgba(26,39,68,0.14)',
          }}
        >
          {shown.length === 0 ? (
            <li
              data-testid="wfh-country-empty"
              style={{ padding: '8px 12px', fontSize: '13px', fontStyle: 'italic',
                       color: dark ? '#9aa6c4' : '#666' }}
            >
              {t('leaveForm.wfhCountryNoMatch')}
            </li>
          ) : shown.map((o, i) => (
            <li
              key={o.stored}
              id={optionId(i)}
              role="option"
              aria-selected={o.stored === value ? 'true' : 'false'}
              data-testid="wfh-country-option"
              // onMouseDown, not onClick: the document mousedown handler above closes the list,
              // and a click would fire after that close and never reach this option.
              onMouseDown={(e) => { e.preventDefault(); choose(o); }}
              onMouseEnter={() => setActive(i)}
              style={{
                padding: '8px 12px', fontSize: '14px', cursor: 'pointer',
                background: i === active ? (dark ? '#2f3d6b' : '#eef2f8') : 'transparent',
                color: dark ? '#e8ecf5' : '#1a2744',
              }}
            >
              {o.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default CountryTypeahead;
